import React from 'react';
import {
  Box,
  Card,
  Divider,
  Grid,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from '@mui/material';
import { AttachMoney, Check, WarningAmber, Sync, ExpandMore, Help } from '@mui/icons-material';
import { formatCurrency, parseCurrency } from '../../common/utils/common.util';
import {
  allFields,
  getDisplayLabel,
  isPrimaryLabel,
  normalizeValue,
  PAYAPP_MISMATCH_COLOR,
  shouldShowComparison,
  splitIntoChunks,
  calculatedOnlyFields,
  payappProcessTooltips,
} from './Payapp.util';
import { ProjectLineItemDTO } from '../project/project.type';
import { CoverPageValues } from '../../context/PayappProcessContext';
import NumericFormatInput from '../general/NumericFormatInput';

interface ProjectBudgetProps {
  coverPageValues: CoverPageValues;
  updateCoverPageValue: (key: string, value: string | number) => void;
  totalsLineItem: ProjectLineItemDTO;
  projectDraw: any;
  setProjectDraw: (projectDraw: any) => void;
  errorFieldNames?: string[];
  originalProjectDraw?: any;
  hideApplicationNumber?: boolean;
  /** When true, hide the budget summary section (e.g. on Merge step) */
  hideBudgetSummary?: boolean;
  /** When true, suppress payapp comparison UI — show only system calculated values (used in manual update mode). */
  hidePayappComparison?: boolean;
  /** When true, this is the second payapp workflow (dual-payapp mode). */
  isSecondPayappFlow?: boolean;
  /** Cover page values from payapp-1, used for combined Requested Amount display in second workflow. */
  firstPayappCoverPageValues?: CoverPageValues | null;
  /** Controlled expanded state for the Budget Summary accordion. */
  expanded?: boolean;
  /** Called when the user toggles the Budget Summary accordion. */
  onExpandedChange?: (expanded: boolean) => void;
}

const getRawCalculatedNumber = (
  label: string,
  coverPageValues: CoverPageValues,
  totalsLineItem: ProjectLineItemDTO,
  originalProjectDraw?: any,
  projectDraw?: any,
  lessPreviousFundingsTopValue?: string, // Add this parameter
): number => {
  const num = (val: string | undefined) => {
    return parseCurrency(val ?? '0');
  };
  switch (label) {
    case 'Original Contract Budget Sum':
      return totalsLineItem.originalBudget;
    case 'Reallocations / Change Orders':
      return totalsLineItem.reallocation + totalsLineItem.changeOrder;
    case 'Contract / Budget Sum to Date':
      return totalsLineItem.originalBudget + totalsLineItem.reallocation + totalsLineItem.changeOrder;
    case 'Total Completed and Stored':
      return totalsLineItem.previousApplication + totalsLineItem.cdccApproved;
    case 'Total Retainage Held': {
      const retainage = totalsLineItem.retainage;
      return retainage > 0 ? -retainage : retainage;
    }

    case '%': {
      const totalCompletedAndStored = totalsLineItem.previousApplication + totalsLineItem.cdccApproved;

      const totalRetainageHeld = (() => {
        const retainage = totalsLineItem.retainage;
        return retainage > 0 ? -retainage : retainage;
      })();

      return totalCompletedAndStored !== 0 ? Number(((Math.abs(totalRetainageHeld) / totalCompletedAndStored) * 100).toFixed(2)) : 0;
    }

    case 'Total Earned Less Retainage':
      return (
        getRawCalculatedNumber('Total Completed and Stored', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw) +
        getRawCalculatedNumber('Total Retainage Held', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw)
      );
    case 'Less Previous Fundings': {
      // Always use updatedLessPreviousFunding - it will be initialized with the calculated value
      return parseCurrency(projectDraw?.budgetSummary?.updatedLessPreviousFunding?.toString() ?? '0');
    }

    case 'Current Payment Due':
      return (
        getRawCalculatedNumber('Total Earned Less Retainage', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw) -
        getRawCalculatedNumber('Less Previous Fundings', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw)
      );
    case 'Balance to Complete Plus Retainage':
      return (
        getRawCalculatedNumber('Contract / Budget Sum to Date', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw) -
        getRawCalculatedNumber('Total Completed and Stored', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw) -
        getRawCalculatedNumber('Total Retainage Held', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw)
      );
    case 'Adjustments by CDCC':
      return num(coverPageValues['Adjustments by CDCC'].toString());
    case 'Total Amount Recommended for Funding':
      return (
        getRawCalculatedNumber('Adjustments by CDCC', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw) +
        getRawCalculatedNumber('Current Payment Due', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw)
      );
    case 'Requested Amount':
      return num(coverPageValues['Requested Amount'].toString());
    case 'Difference':
      return (
        getRawCalculatedNumber('Total Amount Recommended for Funding', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw) -
        getRawCalculatedNumber('Requested Amount', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw)
      );
    case 'Est. Retainage Released This Period':
      return num(coverPageValues['Est. Retainage Released This Period'].toString());
    case 'Total Contract / Budget % Complete': {
      const revisedBudget = totalsLineItem.originalBudget + totalsLineItem.reallocation + totalsLineItem.changeOrder;
      const completedStoredToDate = totalsLineItem.previousApplication + totalsLineItem.cdccApproved;
      return revisedBudget !== 0 ? Number(((completedStoredToDate / revisedBudget) * 100).toFixed(2)) : 0;
    }
    default:
      return 0;
  }
};

const getCalculatedValue = (
  label: string,
  coverPageValues: CoverPageValues,
  totalsLineItem: ProjectLineItemDTO,
  originalProjectDraw?: any,
  projectDraw?: any,
): string => {
  const rawValue = getRawCalculatedNumber(label, coverPageValues, totalsLineItem, originalProjectDraw, projectDraw);
  if (['%', 'Total Contract / Budget % Complete'].includes(label)) {
    const cleaned = rawValue.toString().replace(/\s*%$/, '');
    return `${cleaned} %`;
  }

  return formatCurrency(rawValue.toString());
};

const PayappBudget: React.FC<ProjectBudgetProps> = ({
  coverPageValues,
  updateCoverPageValue,
  projectDraw,
  setProjectDraw,
  totalsLineItem,
  errorFieldNames = [],
  originalProjectDraw,
  hideApplicationNumber = false,
  hideBudgetSummary = false,
  hidePayappComparison = false,
  isSecondPayappFlow = false,
  firstPayappCoverPageValues,
  expanded,
  onExpandedChange,
}) => {
  const [focusedField, setFocusedField] = React.useState<string | null>(null);
  const [internalExpanded, setInternalExpanded] = React.useState(true);
  const accordionExpanded = expanded !== undefined ? expanded : internalExpanded;

  const handleAccordionChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    if (expanded === undefined) {
      setInternalExpanded(isExpanded);
    }
    onExpandedChange?.(isExpanded);
  };

  // Compute fields list based on visibility settings
  // 'Period Beginning' and 'Period Ending' are cover-page-only fields rendered in
  // CoverPageVerification — always exclude them from the Budget Summary.
  const fieldsToRender = React.useMemo(
    () =>
      allFields.filter(f => {
        if (f === 'Period Beginning' || f === 'Period Ending') return false;
        if (f === 'Application Number' && hideApplicationNumber) return false;
        return true;
      }),
    [hideApplicationNumber],
  );

  // Split fields into columns after optional filtering
  const columns = React.useMemo(() => splitIntoChunks(fieldsToRender, 2), [fieldsToRender]);

  // Initialize updatedLessPreviousFunding when component mounts or when originalProjectDraw changes
  React.useEffect(() => {
    if (originalProjectDraw?.budgetSummary && projectDraw?.budgetSummary?.updatedLessPreviousFunding === undefined) {
      const prevMonthFunding = originalProjectDraw.budgetSummary.lessPreviousFundings ?? 0;
      const prevMonthRecommendedAmount = originalProjectDraw.budgetSummary.totalAmountReccForFunding ?? 0;
      const calculatedValue = prevMonthFunding + prevMonthRecommendedAmount;

      setProjectDraw({
        ...projectDraw,
        budgetSummary: {
          ...projectDraw.budgetSummary,
          updatedLessPreviousFunding: calculatedValue,
        },
      });
    }
  }, [originalProjectDraw, projectDraw, setProjectDraw]);

  // Initialize updatedRequestedAmount for manual payapp workflow (no cover page)
  React.useEffect(() => {
    if (hidePayappComparison && projectDraw?.budgetSummary && projectDraw.budgetSummary.updatedRequestedAmount === undefined) {
      setProjectDraw({
        ...projectDraw,
        budgetSummary: {
          ...projectDraw.budgetSummary,
          updatedRequestedAmount: 0,
        },
      });
    }
  }, [hidePayappComparison, projectDraw, setProjectDraw]);

  // Function to auto-adjust the 'Adjustments by CDCC' field
  const handleAutoAdjust = () => {
    const requestedAmount = getRawCalculatedNumber('Requested Amount', coverPageValues, totalsLineItem, originalProjectDraw, projectDraw);
    const currentPaymentDue = getRawCalculatedNumber(
      'Current Payment Due',
      coverPageValues,
      totalsLineItem,
      originalProjectDraw,
      projectDraw,
    );
    const difference = requestedAmount - currentPaymentDue;

    updateCoverPageValue('Adjustments by CDCC', difference);
    setProjectDraw({
      ...projectDraw,
      budgetSummary: {
        ...projectDraw.budgetSummary,
        adjustmentsByCDCC: difference,
      },
    });
  };

  // Fields whose payapp-reported value should be the sum of payapp-1 and payapp-2
  // in the second payapp workflow (they represent per-period amounts, not cumulative).
  const dualPayappSummedFields: string[] = ['Current Payment Due', 'Est. Retainage Released This Period'];

  const renderField = (label: string, index: number, isPrimary: boolean) => {
    const fieldError = errorFieldNames.includes(label);

    let editableRawValue = coverPageValues[label]?.toString() ?? '';

    // Ensure 'Total Retainage Held' always has negative value for display
    if (label === 'Total Retainage Held') {
      const normalized = normalizeValue(editableRawValue);
      if (!normalized.startsWith('-')) {
        editableRawValue = `-${normalized}`;
      }
    }

    // In the second payapp workflow, per-period fields (e.g. Current Payment Due)
    // should display and compare against the combined P1+P2 value.
    // We compute the effective numeric value once and use it for both display and
    // comparison, avoiding the conflict between formatCurrency (needs a plain
    // numeric string) and normalizeValue (needs a formatted currency string).
    let effectivePayappNum: number | null = null;
    if (isSecondPayappFlow && firstPayappCoverPageValues && dualPayappSummedFields.includes(label)) {
      const p1 = parseCurrency(normalizeValue(firstPayappCoverPageValues[label]?.toString() ?? '0') || '0');
      const p2 = parseCurrency(normalizeValue(editableRawValue) || '0');
      effectivePayappNum = p1 + p2;
    }

    const formattedEditableValue =
      effectivePayappNum !== null
        ? formatCurrency(effectivePayappNum.toString())
        : !editableRawValue
          ? '0'
          : label === '%'
            ? `${editableRawValue.toString()} %`
            : focusedField === label
              ? editableRawValue.toString()
              : formatCurrency(editableRawValue);

    const calculatedSystemValue = getCalculatedValue(label, coverPageValues, totalsLineItem, originalProjectDraw, projectDraw);

    // For comparison, use the numeric sum directly (avoids OCR-fix heuristics in normalizeValue)
    const calculatedNum = getRawCalculatedNumber(label, coverPageValues, totalsLineItem, originalProjectDraw, projectDraw);
    const payappNum = effectivePayappNum !== null ? effectivePayappNum : parseCurrency(normalizeValue(editableRawValue) || '0');
    const match = Math.abs(payappNum - calculatedNum) < 0.005;
    const isMinorDiscrepancy = !match && Math.abs(payappNum - calculatedNum) < 1.0;
    const difference = Math.abs(payappNum - calculatedNum).toFixed(2);
    const differenceFormatted = label === '%' ? `${difference} %` : formatCurrency(difference);

    // In manual payapp workflow, Requested Amount has no cover page source — make it editable
    if (label === 'Requested Amount' && hidePayappComparison) {
      const currentValue = projectDraw?.budgetSummary?.updatedRequestedAmount?.toString() ?? '0';

      return (
        <React.Fragment key={label}>
          <Box display='flex' flexDirection='row' gap={0.5} mb={0}>
            <Box display='flex' justifyContent='space-between' alignItems='flex-start' flexDirection={'column'} flex={1}>
              <Typography
                variant='subtitle2'
                fontWeight={isPrimary ? 600 : 400}
                color={fieldError ? 'error' : 'text.primary'}
                sx={{ flex: 1 }}
              >
                {label}
              </Typography>
            </Box>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Stack alignItems={'flex-end'}>
                <TextField
                  variant='outlined'
                  size='medium'
                  value={currentValue}
                  onChange={e => {
                    setProjectDraw({
                      ...projectDraw,
                      budgetSummary: {
                        ...projectDraw.budgetSummary,
                        updatedRequestedAmount: e.target.value,
                      },
                    });
                  }}
                  onFocus={() => setFocusedField(label)}
                  onBlur={() => {
                    setFocusedField(null);
                    const parsed = parseCurrency(projectDraw?.budgetSummary?.updatedRequestedAmount?.toString() ?? '0');
                    updateCoverPageValue('Requested Amount', parsed);
                  }}
                  sx={{
                    width: '160px',
                    input: {
                      fontSize: '1rem',
                      textAlign: 'right',
                      paddingRight: '8px',
                      paddingTop: '5px',
                      paddingBottom: '5px',
                    },
                  }}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                    startAdornment: (
                      <Typography variant='caption' sx={{ mr: 0.5 }}>
                        $
                      </Typography>
                    ),
                  }}
                />
              </Stack>
            </Box>
          </Box>
          {index < 7 && <Divider />}
        </React.Fragment>
      );
    }

    if (!shouldShowComparison(label) || (hidePayappComparison && label !== 'Less Previous Fundings')) {
      let valueToDisplay = calculatedOnlyFields.includes(label) || hidePayappComparison ? calculatedSystemValue : formattedEditableValue;

      // In the second payapp workflow's review step, Requested Amount (top "Payapp" value)
      // should be the sum of payapp-1 and payapp-2 requested amounts.
      if (label === 'Requested Amount' && isSecondPayappFlow && firstPayappCoverPageValues) {
        const p1Raw = firstPayappCoverPageValues['Requested Amount']?.toString() ?? '0';
        const p2Raw = coverPageValues['Requested Amount']?.toString() ?? '0';
        const p1Norm = normalizeValue(p1Raw);
        const p2Norm = normalizeValue(p2Raw);
        const sum = parseCurrency(p1Norm || '0') + parseCurrency(p2Norm || '0');
        valueToDisplay = formatCurrency(sum.toString());
      }

      // In the second payapp workflow, Difference = Total Amount Recommended for Funding
      // minus the COMBINED (P1+P2) Requested Amount. getRawCalculatedNumber uses only the
      // P2 cover page value for Requested Amount, so we override it here.
      if (label === 'Difference' && isSecondPayappFlow && firstPayappCoverPageValues) {
        const p1Requested = parseCurrency(normalizeValue(firstPayappCoverPageValues['Requested Amount']?.toString() ?? '0') || '0');
        const p2Requested = parseCurrency(normalizeValue(coverPageValues['Requested Amount']?.toString() ?? '0') || '0');
        const combinedRequested = p1Requested + p2Requested;
        const totalForFunding = getRawCalculatedNumber(
          'Total Amount Recommended for Funding',
          coverPageValues,
          totalsLineItem,
          originalProjectDraw,
          projectDraw,
        );
        valueToDisplay = formatCurrency((totalForFunding - combinedRequested).toString());
      }
      return (
        <React.Fragment key={label}>
          <Box display='flex' flexDirection='row' gap={0.5} mb={0}>
            <Box display='flex' justifyContent='space-between' alignItems='flex-start' flexDirection={'column'} flex={1}>
              <Typography
                variant='subtitle2'
                fontWeight={isPrimary ? 600 : 400}
                color={fieldError ? 'error' : 'text.primary'}
                sx={{ flex: 1 }}
              >
                {getDisplayLabel(label)}
                {label === 'Adjustments by CDCC' && (
                  <Tooltip title='Auto-adjust to make difference zero' placement='top'>
                    <IconButton size='small' onClick={handleAutoAdjust} sx={{ ml: 0.5, p: 0.5 }} color='primary'>
                      <Sync fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Typography>
            </Box>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Stack alignItems={'flex-end'}>
                <Typography variant='body2' sx={{ paddingRight: '10px' }}>
                  {valueToDisplay}
                </Typography>
              </Stack>
            </Box>
          </Box>
          {index < 7 && <Divider />}
        </React.Fragment>
      );
    }

    // Special handling for "Less Previous Fundings" - make top value editable
    if (label === 'Less Previous Fundings') {
      // Always use the updated value - it will be initialized with the calculated value
      const currentTopValue = projectDraw?.budgetSummary?.updatedLessPreviousFunding?.toString() ?? '0';
      // Manual workflow: compare the entered value against last month's LPF + recommended funding
      const expectedLPF =
        (originalProjectDraw?.budgetSummary?.lessPreviousFundings ?? 0) +
        (originalProjectDraw?.budgetSummary?.totalAmountReccForFunding ?? 0);
      const manualLPFDifference = parseCurrency(currentTopValue) - expectedLPF;
      const showManualLPFDifference = hidePayappComparison && Math.abs(manualLPFDifference) >= 0.01;
      const manualLPFTooltip = `This Previous Fundings value is different from what we expected based on last month's recommendation for funding. Expected: ${formatCurrency(expectedLPF.toFixed(2))}. When you save, the difference will automatically be added to the Previous Fundings table so that the inspector can call the bank and resolve.`;

      return (
        <React.Fragment key={label}>
          <Box display='flex' flexDirection='row' gap={0.5} mb={0}>
            <Box display='flex' justifyContent='space-between' alignItems='flex-start' flexDirection={'column'} flex={1}>
              <Typography
                variant='subtitle2'
                fontWeight={isPrimary ? 600 : 400}
                color={fieldError ? 'error' : 'text.primary'}
                sx={{ flex: 1 }}
              >
                {label}
              </Typography>
              {!hidePayappComparison && (
                <Typography
                  variant='body2'
                  sx={{ fontStyle: 'italic', flex: 1, color: isMinorDiscrepancy ? '#db6e21' : PAYAPP_MISMATCH_COLOR }}
                >
                  {!match && `(Difference = ${differenceFormatted})`}
                </Typography>
              )}
              {showManualLPFDifference && (
                <Tooltip title={manualLPFTooltip} placement='top'>
                  <Typography variant='body2' sx={{ fontStyle: 'italic', flex: 1, color: PAYAPP_MISMATCH_COLOR, cursor: 'help' }}>
                    {`(Difference = ${formatCurrency(Math.abs(manualLPFDifference).toFixed(2))})`}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            <Box display='flex' justifyContent='space-between' alignItems='center'>
              {showManualLPFDifference && (
                <Tooltip title={manualLPFTooltip} placement='top'>
                  <Stack mr={2} sx={{ cursor: 'help' }}>
                    <WarningAmber fontSize='small' color='error' />
                  </Stack>
                </Tooltip>
              )}
              {!hidePayappComparison && (
                <Stack mr={2}>
                  {match ? (
                    <Check fontSize='small' color='success' />
                  ) : (
                    <WarningAmber
                      fontSize='small'
                      sx={{ color: isMinorDiscrepancy ? '#df7b16' : undefined }}
                      color={isMinorDiscrepancy ? undefined : 'error'}
                    />
                  )}
                </Stack>
              )}
              <Stack alignItems={'flex-end'}>
                {/* Editable top value for Less Previous Fundings */}
                <TextField
                  variant='outlined'
                  size='medium'
                  value={currentTopValue}
                  onChange={e => {
                    const newValue = e.target.value;
                    // Update the originalProjectDraw with the new value
                    setProjectDraw({
                      ...projectDraw,
                      budgetSummary: {
                        ...projectDraw.budgetSummary,
                        updatedLessPreviousFunding: newValue,
                      },
                    });
                  }}
                  onFocus={() => setFocusedField(label)}
                  onBlur={() => setFocusedField(null)}
                  sx={{
                    width: '160px',
                    input: {
                      fontSize: '1rem',
                      textAlign: 'right',
                      paddingRight: '8px',
                      paddingTop: '5px',
                      paddingBottom: '5px',
                    },
                  }}
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                    startAdornment: (
                      <Typography variant='caption' sx={{ mr: 0.5 }}>
                        $
                      </Typography>
                    ),
                  }}
                />
                {/* Bottom value - should be the original cover page value, NOT calculated */}
                {!hidePayappComparison && (
                  <Typography variant='body2' fontSize={'14px'} sx={{ paddingRight: '10px', mt: 1 }}>
                    {formattedEditableValue}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
          {index < 7 && <Divider />}
        </React.Fragment>
      );
    }

    // Default rendering for other comparison fields
    // Note: for dualPayappSummedFields in second payapp flow, formattedEditableValue already
    // reflects the P1+P2 sum (editableRawValue was overridden above), so no extra override needed.
    const bottomValue = formattedEditableValue;

    return (
      <React.Fragment key={label}>
        <Box display='flex' flexDirection='row' gap={0.5} mb={0}>
          <Box display='flex' justifyContent='space-between' alignItems='flex-start' flexDirection={'column'} flex={1}>
            <Typography
              variant='subtitle2'
              fontWeight={isPrimary ? 600 : 400}
              color={fieldError ? 'error' : 'text.primary'}
              sx={{ flex: 1 }}
            >
              {getDisplayLabel(label)}
            </Typography>
            <Typography
              variant='body2'
              sx={{ fontStyle: 'italic', flex: 1, color: isMinorDiscrepancy ? '#db6e21' : PAYAPP_MISMATCH_COLOR }}
            >
              {!match && `(Difference = ${differenceFormatted})`}
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Stack mr={2}>
              {match ? (
                <Check fontSize='small' color='success' />
              ) : (
                <WarningAmber
                  fontSize='small'
                  sx={{ color: isMinorDiscrepancy ? '#df7b16' : undefined }}
                  color={isMinorDiscrepancy ? undefined : 'error'}
                />
              )}
            </Stack>
            <Stack alignItems={'flex-end'}>
              <Typography variant='body2' sx={{ paddingRight: '10px' }}>
                {calculatedSystemValue}
              </Typography>
              <Typography variant='body2' fontSize={'14px'} sx={{ paddingRight: '10px' }}>
                {bottomValue}
              </Typography>
            </Stack>
          </Box>
        </Box>
        {index < 7 && <Divider />}
      </React.Fragment>
    );
  };

  if (hideBudgetSummary) return null;

  return (
    <div>
      <Accordion expanded={accordionExpanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls='budget-summary-content' id='budget-summary-header'>
          <Box display='flex' alignItems='center'>
            <AttachMoney color='primary' sx={{ fontSize: '24px !important', fontWeight: '700 !important' }} />
            <Typography variant='h5' color='primary' sx={{ ml: 0.5 }}>
              G702/3 Summary
            </Typography>
            <Tooltip
              title={payappProcessTooltips.CompareAgainstLastMonthBudgetSummary}
              placement='bottom'
              arrow={false}
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 'none',
                    padding: '12px 16px',
                  },
                },
              }}
            >
              <Help sx={{ ml: 1, cursor: 'pointer', fontSize: '20px' }} />
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {columns.map((columnFields, colIndex) => (
              <Grid size={{ xl: 6 }} key={`column-${colIndex}`} sx={{ minHeight: '100%' }}>
                <Card elevation={colIndex === 0 ? 1 : 3} style={{ padding: '16px', height: '100%' }}>
                  {columnFields.map((field, fieldIndex) => renderField(field, fieldIndex, isPrimaryLabel(field)))}
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default PayappBudget;
